import { useState, useEffect } from "react";
import { Briefcase, Mail, Pencil, Power, Trash2 } from "lucide-react";
import "./HRJobManagementPage.css";

import {
  getJobs,
  createJob,
  updateJob,
  deleteJob,
  sendReferralEmails,
} from "../../services/jobService";

const HRJobManagementPage = () => {
  const [showAllJobsPage, setShowAllJobsPage] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const jobsPerPage = 10;

  const [form, setForm] = useState({
    job_code: "",
    title: "",
    description: "",
    location: "",
    experience: "",
    employment_type: "FULL_TIME",
    is_active: true,
  });

  // ==============================
  // FETCH JOBS
  // ==============================
  const fetchJobs = async (page = 1) => {
    try {
      const response = await getJobs({ page, limit: jobsPerPage });

      console.log("Jobs API Response:", response);

      const jobList = response.data || response.jobs || [];

      setJobs(Array.isArray(jobList) ? jobList : []);
      setCurrentPage(response.pagination?.currentPage || page);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      setJobs([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (showAllJobsPage) {
      fetchJobs(currentPage);
    }
  }, [currentPage, showAllJobsPage]);

  // ==============================
  // INPUT CHANGE
  // ==============================
  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  };

  // ==============================
  // RESET FORM
  // ==============================
  const resetForm = () => {
    setEditingId(null);

    setForm({
      job_code: "",
      title: "",
      description: "",
      location: "",
      experience: "",
      employment_type: "FULL_TIME",
      is_active: true,
    });
  };

  // ==============================
  // CREATE / UPDATE JOB
  // ==============================
  const onSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      experience: Number(form.experience),
    };

    try {
      if (editingId) {
        await updateJob(editingId, payload);
      } else {
        await createJob(payload);
      }

      fetchJobs(showAllJobsPage ? currentPage : 1);
      resetForm();
    } catch (error) {
      console.error("Error saving job:", error);
    }
  };

  // ==============================
  // EDIT JOB
  // ==============================
  const onEdit = (job) => {
    setEditingId(job.id);

    setForm({
      job_code: job.job_code || "",
      title: job.title || "",
      description: job.description || "",
      location: job.location || "",
      experience: job.experience || "",
      employment_type: job.employment_type || "FULL_TIME",
      is_active: job.is_active ?? true,
    });

    setShowAllJobsPage(true);
  };

  // ==============================
  // DELETE JOB
  // ==============================
  const onDeleteJob = async (id) => {
    try {
      await deleteJob(id);
      fetchJobs(showAllJobsPage ? currentPage : 1);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  // ==============================
  // TOGGLE STATUS
  // ==============================
  const onToggleStatus = async (job) => {
    try {
      const payload = {
        job_code: job.job_code,
        title: job.title,
        description: job.description,
        location: job.location,
        experience: Number(job.experience),
        employment_type: job.employment_type,
        is_active: !job.is_active,
      };

      await updateJob(job.id, payload);
      fetchJobs(showAllJobsPage ? currentPage : 1);
    } catch (error) {
      console.error("Status toggle failed:", error);
    }
  };

  // ==============================
  // SEND REFERRAL EMAILS
  // ==============================
  const onSendReferralEmails = async (job) => {
    try {
      const response = await sendReferralEmails(job.id);
      alert(
        `${response.message || "Referral emails processed"}\nSent: ${
          response.sent ?? 0
        }\nFailed: ${response.failed ?? 0}`,
      );
    } catch (error) {
      alert(
        error.response?.data?.message || "Failed to send referral emails",
      );
    }
  };

  // ==============================
  // ALL JOBS PAGE
  // ==============================
  if (showAllJobsPage) {
    return (
      <section className="job-page all-jobs-page">
        <article className="hr-page-card">
          <div className="job-title-row">
            <div className="hr-page-title">
              <Briefcase size={20} />
              <h2>All Jobs</h2>
            </div>

            <button
              className="secondary-btn"
              onClick={() => {
                resetForm();
                setCurrentPage(1);
                setShowAllJobsPage(false);
              }}
            >
              Back
            </button>
          </div>

          {editingId && (
            <form className="job-form" onSubmit={onSubmit}>
              <input
                name="job_code"
                value={form.job_code}
                onChange={onInputChange}
                placeholder="Job Code"
              />

              <input
                name="title"
                value={form.title}
                onChange={onInputChange}
                placeholder="Job Title"
              />

              <input
                name="location"
                value={form.location}
                onChange={onInputChange}
                placeholder="Location"
              />

              <input
                name="experience"
                type="number"
                value={form.experience}
                onChange={onInputChange}
                placeholder="Experience"
              />

              <select
                name="employment_type"
                value={form.employment_type}
                onChange={onInputChange}
              >
                <option value="FULL_TIME">Full Time</option>
                <option value="PART_TIME">Part Time</option>
                <option value="CONTRACT">Contract</option>
                <option value="INTERN">Intern</option>
              </select>

              <label className="job-checkbox">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={onInputChange}
                />
                Is Active
              </label>

              <textarea
                name="description"
                value={form.description}
                onChange={onInputChange}
                placeholder="Job Description"
              />

              <div className="job-form-actions">
                <button type="submit" className="primary-btn">
                  Update Job
                </button>

                <button
                  type="button"
                  className="secondary-btn"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="job-list">
            {jobs.map((job) => (
              <div className="job-row" key={job.id}>
                <div className="job-info">
                  <h3>
                    {job.title} ({job.job_code || "N/A"})
                  </h3>

                  <p>
                    {job.location} | {job.employment_type} | EXP:{" "}
                    {job.experience}
                  </p>

                  <p>{job.description}</p>

                  <span
                    className={`status-badge status-${
                      job.is_active ? "active" : "inactive"
                    }`}
                  >
                    {job.is_active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className="job-actions">
                  <button className="icon-action" onClick={() => onEdit(job)}>
                    <Pencil size={15} /> Edit
                  </button>

                  <button
                    className="icon-action referral-action"
                    onClick={() => onSendReferralEmails(job)}
                  >
                    <Mail size={15} /> Send Referral Emails
                  </button>

                  <button
                    className="icon-action danger"
                    onClick={() => onDeleteJob(job.id)}
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="job-pagination">
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="pagination-status">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="pagination-btn"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </article>
      </section>
    );
  }

  // ==============================
  // MAIN JOB MANAGEMENT PAGE
  // ==============================
  return (
    <section className="job-page">
      <article className="hr-page-card">
        <div className="hr-page-title">
          <Briefcase size={20} />
          <h2>Job Management</h2>
        </div>

        <form className="job-form" onSubmit={onSubmit}>
          <input
            name="job_code"
            value={form.job_code}
            onChange={onInputChange}
            placeholder="Job Code"
          />

          <input
            name="title"
            value={form.title}
            onChange={onInputChange}
            placeholder="Job Title"
          />

          <input
            name="location"
            value={form.location}
            onChange={onInputChange}
            placeholder="Location"
          />

          <input
            type="number"
            name="experience"
            value={form.experience}
            onChange={onInputChange}
            placeholder="Experience"
          />

          <select
            name="employment_type"
            value={form.employment_type}
            onChange={onInputChange}
          >
            <option value="FULL_TIME">Full Time</option>
            <option value="PART_TIME">Part Time</option>
            <option value="CONTRACT">Contract</option>
            <option value="INTERN">Intern</option>
          </select>

          <label className="job-checkbox">
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={onInputChange}
            />
            Is Active
          </label>

          <textarea
            name="description"
            value={form.description}
            onChange={onInputChange}
            placeholder="Job Description"
          />

          <div className="job-form-actions">
            <button type="submit" className="primary-btn">
              Create Job
            </button>
          </div>
        </form>
      </article>

      <article className="hr-page-card">
        <div className="job-title-row">
          <div className="hr-page-title">
            <Briefcase size={20} />
            <h2>Existing Job Posts</h2>
          </div>

          <button
            className="secondary-btn"
            onClick={() => {
              setCurrentPage(1);
              setShowAllJobsPage(true);
            }}
          >
            All Posts
          </button>
        </div>

        <div className="job-list">
          {jobs.map((job) => (
            <div className="job-row" key={job.id}>
              <div className="job-info">
                <h3>
                  {job.title} ({job.job_code || "N/A"})
                </h3>

                <p>
                  {job.location} | {job.employment_type} | EXP :{" "}
                  {job.experience}
                </p>

                <p>{job.description}</p>

                <span
                  className={`status-badge status-${
                    job.is_active ? "active" : "inactive"
                  }`}
                >
                  {job.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="job-actions">
                <button
                  className="icon-action"
                  onClick={() => onToggleStatus(job)}
                >
                  <Power size={15} />
                </button>

                <button
                  className="icon-action referral-action"
                  onClick={() => onSendReferralEmails(job)}
                >
                  <Mail size={15} /> Send Referral Emails
                </button>
              </div>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
};

export default HRJobManagementPage;
 
